/**
 * Calendar API Client
 * Handles all interactions with the calendar backend API
 */

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

/**
 * Calendar event types
 */
export type CalendarEventType =
  | 'llc_renewal'
  | 'birthday'
  | 'appointment'
  | 'out_of_office'
  | 'reminder'
  | 'deadline'
  | 'office_closed';
export type CalendarSyncStatus = 'pending' | 'synced' | 'failed' | 'deleted';
export type CalendarPriority = 'low' | 'medium' | 'high';

/**
 * Calendar event data
 */
export interface CalendarEvent {
  id: string;
  google_event_id: string;
  event_type: CalendarEventType;
  spreadsheet_row_id: string | null;
  employee_id: string | null; // Deprecated: kept for backward compatibility
  employee_ids: string[]; // Array of employee IDs for multiple assignments
  client_id?: string | null; // Reference to client this event is for
  event_title: string;
  event_date: string;
  event_end_date?: string; // End date for multi-day events
  /** True start date, set on per-day copies made by expandMultiDayEvents */
  original_event_date?: string;
  event_description: string | null;
  start_time?: string | null; // HH:MM format
  end_time?: string | null; // HH:MM format
  business_name: string | null;
  federal_tax_id: string | null;
  owner_name: string | null;
  owner_phone: string | null;
  owner_email: string | null;
  employee_name: string | null;
  employee_email: string | null;
  priority_level: CalendarPriority | null;
  sync_status: CalendarSyncStatus;
  sync_error: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
  synced_at: string | null;
  color_id?: string | null; // Google Calendar color ID for appointments
  appointment_type?: string | null; // Type of appointment: tax, consulting, 1099, other
  is_walk_in?: boolean;
  is_client_drop_off?: boolean;
  is_email?: boolean;
  is_new_client?: boolean;
  tiffany_only?: boolean | null; // Flag for appointments specifically requesting Tiffany
  is_zoom?: boolean | null; // Flag for Zoom appointments
  meeting_provider?: string | null; // Virtual meeting provider: zoom, teams
  meeting_url?: string | null; // Join URL for virtual meeting
  meeting_id?: string | null; // Meeting ID for virtual meeting
  meeting_password?: string | null; // Password for virtual meeting
}

/**
 * LLC Renewal event data for syncing
 */
export interface LLCRenewalSyncData {
  spreadsheet_row_id: string;
  business_name: string;
  federal_tax_id?: string;
  owner_name?: string;
  owner_phone?: string;
  owner_email?: string;
  renewal_date: string;
}

/**
 * Birthday event data for syncing
 */
export interface BirthdaySyncData {
  employee_id: string;
  employee_name: string;
  employee_email: string;
  birthday: string;
}

/**
 * Sync response
 */
export interface SyncResponse {
  success: boolean;
  google_event_id?: string;
  sync_log_id?: string;
  error?: string;
}

/**
 * Helper function to make API requests with proper headers
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        errorData.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return response.json();
}

/**
 * Sync a single LLC renewal to Google Calendar
 */
export async function syncLLCRenewal(
  data: LLCRenewalSyncData
): Promise<SyncResponse> {
  return apiRequest<SyncResponse>('/api/calendar/sync-llc-renewal', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Sync a single birthday to Google Calendar
 */
export async function syncBirthday(
  data: BirthdaySyncData
): Promise<SyncResponse> {
  return apiRequest<SyncResponse>('/api/calendar/sync-birthday', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a calendar event
 */
export async function deleteCalendarEvent(
  googleEventId: string
): Promise<SyncResponse> {
  return apiRequest<SyncResponse>(`/api/calendar/event/${googleEventId}`, {
    method: 'DELETE',
  });
}

/**
 * Get calendar events for a date range
 */
export async function getCalendarEvents(
  startDate: string,
  endDate: string
): Promise<{ success: boolean; events?: any[]; error?: string }> {
  return apiRequest<{ success: boolean; events?: any[]; error?: string }>(
    `/api/calendar/events?start=${startDate}&end=${endDate}`,
    {
      method: 'GET',
    }
  );
}

/**
 * Get sync logs with optional filters
 */
export async function getSyncLogs(filters?: {
  event_type?: CalendarEventType;
  sync_status?: CalendarSyncStatus;
  limit?: number;
}): Promise<{ success: boolean; logs: CalendarEvent[] }> {
  const params = new URLSearchParams();

  if (filters?.event_type) {
    params.append('event_type', filters.event_type);
  }

  if (filters?.sync_status) {
    params.append('sync_status', filters.sync_status);
  }

  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }

  const queryString = params.toString();
  const endpoint = queryString
    ? `/api/calendar/sync-logs?${queryString}`
    : '/api/calendar/sync-logs';

  return apiRequest<{ success: boolean; logs: CalendarEvent[] }>(endpoint, {
    method: 'GET',
  });
}

/**
 * Get calendar events from Google Calendar for a date range
 * This will expand recurring events into individual instances
 */
export async function getCalendarEventsFromGoogle(
  startDate: string,
  endDate: string
): Promise<{ success: boolean; events: any[] }> {
  const params = new URLSearchParams();
  params.append('start', startDate);
  params.append('end', endDate);

  const endpoint = `/api/calendar/events?${params.toString()}`;

  return apiRequest<{ success: boolean; events: any[] }>(endpoint, {
    method: 'GET',
  });
}

/**
 * Sync a spreadsheet row to Google Calendar
 * Automatically detects if it's an LLC renewal based on the row data
 */
export async function syncEventToCalendar(rowData: {
  id: string;
  data?: { [key: string]: any };
  [key: string]: any;
}): Promise<SyncResponse> {
  const llcDate = rowData.data?.['LLC Renewal/Reinstatement Date'];
  const businessName = rowData.data?.['Legal Business Name'];

  if (!llcDate || !businessName) {
    return {
      success: false,
      error:
        'Missing required fields: LLC Renewal/Reinstatement Date or Legal Business Name',
    };
  }

  return syncLLCRenewal({
    spreadsheet_row_id: rowData.id,
    business_name: businessName,
    federal_tax_id: rowData.data?.['Federal Tax ID'],
    owner_name: rowData.data?.['Owner Name'],
    owner_phone: rowData.data?.['Owner Phone'],
    owner_email: rowData.data?.['Owner Email'],
    renewal_date: llcDate,
  });
}

/**
 * Sync birthday to Google Calendar (alias for syncBirthday)
 * Used in the Settings page when an employee updates their birthday
 */
export async function syncBirthdayToCalendar(data: {
  employee_id: string;
  employee_name: string;
  birthday: string;
}): Promise<SyncResponse> {
  return syncBirthday({
    ...data,
    employee_email: '', // Email is optional for birthday events
  });
}

/**
 * Delete a birthday event by employee ID
 */
export async function deleteBirthdayByEmployeeId(
  employeeId: string
): Promise<SyncResponse> {
  return apiRequest<SyncResponse>(`/api/calendar/birthday/${employeeId}`, {
    method: 'DELETE',
  });
}

/**
 * Create a new appointment event
 */
export async function createAppointmentEvent(data: {
  event_title: string;
  event_description?: string;
  event_date: string;
  event_end_date?: string;
  employee_id?: string | null;
  employee_ids?: string[];
  color_id?: string | null;
  visibility?: string;
  transparency?: string;
  reminders?: string;
  event_category?: string;
  start_time?: string;
  end_time?: string;
  client_id?: string;
}): Promise<SyncResponse> {
  return apiRequest<SyncResponse>('/api/calendar/appointment', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an appointment event
 */
export async function updateAppointmentEvent(
  googleEventId: string,
  data: {
    event_title?: string;
    event_description?: string;
    event_date?: string;
    event_end_date?: string;
    employee_id?: string | null; // Deprecated: kept for backward compatibility
    employee_ids?: string[]; // Array of employee IDs for multiple assignments
    color_id?: string | null;
    visibility?: string;
    transparency?: string;
    reminders?: string;
    event_category?: string;
    start_time?: string;
    end_time?: string;
  }
): Promise<SyncResponse> {
  return apiRequest<SyncResponse>(
    `/api/calendar/appointment/${googleEventId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Sync a reminder to Google Calendar
 */
export async function syncReminderToCalendar(data: {
  reminder_id: string;
  title: string;
  description: string;
  due_date: string;
}): Promise<SyncResponse> {
  return apiRequest<SyncResponse>('/api/calendar/sync-reminder', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Sync an announcement to Google Calendar
 */
export async function syncAnnouncementToCalendar(data: {
  announcement_id: string;
  title: string;
  description: string;
  event_date: string;
}): Promise<SyncResponse> {
  return apiRequest<SyncResponse>('/api/calendar/sync-announcement', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
